import { sportslineGraphQLClient } from '@lib/graphql-client';
import { ExpertPick } from '@lib/graphql-types';
import { Component, Host, Prop, State, h, Element } from '@stencil/core';
import { fromExpertPickToSportslinePickProps, getCardTitle, getPropertyGameHref } from '@utils/expertPickUtils';
import { observeVisibilityAndTrigger } from '@utils/html';
import { ClientName, SportslinePickProps } from 'src/types';

@Component({
  tag: 'sportsline-promoted-expert-picks',
  styleUrl: 'sportsline-promoted-expert-picks.css',
  shadow: true,
})
export class SportslinePromotedExpertPicks {
  @State() expertPicks: Array<
    SportslinePickProps & {
      iSubscriber?: ExpertPick['isSubscriber'];
    }
  > | null;

  @State() loading: boolean = true;
  @State() error: string | null = null;

  /**
   * The name of the client, used to customize links and text based on the client.
   */
  @Prop() clientName?: ClientName;

  /**
   * Title for the card to replace the default.
   */
  @Prop() cardTitle?: string;

  /**
   * The text to be displayed in the upsell section to replace the default based on the clientName.
   */
  @Prop() upsellText?: string;

  /**
   * The URL to the subscription page.
   */
  @Prop() subscribeLink?: string;

  /**
   * League abbreviation for the game, used to generate the game URL.
   */
  @Prop() leagueAbbr?: string;

  /**
   * The number of expert picks to display.
   * It will max out at 5
   */
  @Prop() first: number = 5;

  @Element() el: HTMLSportslinePromotedExpertPicksElement;

  private client = sportslineGraphQLClient();

  private async fetch() {
    this.loading = true;
    this.error = null;

    await this.client.dataFetcher
      .SportslineWebComponent_PromotedExpertPicks({
        first: this.first,
        ...(this.leagueAbbr && {
          filter: {
            leagues: [this.leagueAbbr],
          },
        }),
      })
      .then(response => {
        const data = response.data;
        if (Boolean(data?.promotedExpertPicks?.edges?.length)) {
          this.expertPicks =
            [...data.promotedExpertPicks.edges]
              .sort((a, b) => {
                // Sort by some criteria, e.g., by date
                try {
                  return `${b.node.game.scheduledTime}`.localeCompare(`${a.node.game.scheduledTime}`);
                } catch (error) {
                  console.warn('Error comparing dates:', error);
                  // If date comparison fails, fallback to a default value
                }
                return 0; // Fallback if date comparison fails
              })
              .map(({ node }) => fromExpertPickToSportslinePickProps(node)) ?? null;
        }
      })
      .catch((error: Error) => {
        console.warn('Error fetching data:', error);
        // TODO: Move message this to a common place so it can be reused
        this.error = 'An error occurred while fetching data. Please try again later.';
      })
      .finally(() => {
        this.loading = false;
      });
  }

  async componentWillLoad() {
    observeVisibilityAndTrigger({
      element: this.el,
      callback: async () => {
        await this.fetch();
      },
    });
  }

  render() {
    if (!Boolean(this.expertPicks?.length) && !this.loading) {
      if (Boolean(this.error)) {
        return (
          <Host>
            <slot></slot>
            <section>
              <span>{this.error}</span>
            </section>
          </Host>
        );
      }
      return null;
    }

    if (this.loading) {
      return (
        <Host>
          <div
            class={{
              'sportsline-promoted-expert-picks--loading': true,
            }}
          >
            <slot></slot>
            <sportsline-circular-progress size="sm" />
          </div>
        </Host>
      );
    }

    return (
      <Host>
        <slot></slot>
        {this.expertPicks.map(expertPick => (
          <sportsline-free-expert-pick-card
            key={expertPick.id}
            pick={expertPick}
            gameUrl={getPropertyGameHref({
              clientName: this.clientName,
              leagueAbbr: expertPick.gameInfo?.league.abbrev,
              gameAbbr: expertPick.gameInfo?.abbrev,
            })}
            cardTitle={getCardTitle({
              title: this.cardTitle,
              leagueAbbr: expertPick.gameInfo?.league?.abbrev,
            })}
          />
        ))}
      </Host>
    );
  }
}
